import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { captureSentryException } from '../config/sentry';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Send error to Sentry with React component context
    try {
      captureSentryException(error, {
        react: {
          componentStack: errorInfo.componentStack,
        },
        errorBoundary: {
          errorCount: this.state.errorCount + 1,
        },
      });
    } catch (sentryError) {
      console.error('Failed to send error to Sentry:', sentryError);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorCount } = this.state;
      const { fallbackTitle, fallbackMessage, showDetails } = this.props;

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-red-900">
                    {fallbackTitle || 'Something went wrong'}
                  </CardTitle>
                  <CardDescription className="text-red-700">
                    {fallbackMessage ||
                      'We encountered an unexpected error. Please try again or return to the homepage.'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Error count warning */}
              {errorCount > 2 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-900">
                    <strong>Multiple errors detected.</strong> If this problem persists,
                    please contact support or try clearing your browser cache.
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button onClick={this.handleReset} className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </Button>
                <Button variant="outline" onClick={this.handleGoHome} className="gap-2">
                  <Home className="w-4 h-4" />
                  Go to Homepage
                </Button>
              </div>

              {/* Error details (only in development or when showDetails is true) */}
              {(process.env.NODE_ENV === 'development' || showDetails) && error && (
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    Technical Details (for developers)
                  </summary>
                  <div className="mt-3 p-4 bg-gray-900 rounded-lg overflow-auto">
                    <div className="text-red-400 font-mono text-xs mb-3">
                      <strong>Error:</strong> {error.toString()}
                    </div>
                    {errorInfo && errorInfo.componentStack && (
                      <div className="text-gray-300 font-mono text-xs">
                        <strong>Component Stack:</strong>
                        <pre className="mt-2 whitespace-pre-wrap">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Help text */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  If you continue to experience issues, please contact our support team
                  with details about what you were doing when this error occurred.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
